import { Link, useLocation } from 'react-router-dom'
import Logo from './Logo'
import Bee from './Bee/Bee'
import { WHATSAPP_NUMBER, CONTACT_EMAIL, TIKTOK_URL, buildWhatsAppLink } from '../data/config'
import { resolveSectionHref } from '../lib/sectionAnchors'

// anchor: resolved per-page via resolveSectionHref (lib/sectionAnchors.js)
// instead of a bare "#id" — a bare hash silently does nothing on any page
// that doesn't actually contain that section (e.g. #pricing on every
// campaign page, or #transformations/#faq on /onlinegrowth).
const COLUMNS = [
  {
    title: 'Services',
    links: [
      { label: 'All Services', href: '/services' },
      { label: 'Industries', href: '/industries' },
      { label: 'Locations', href: '/locations' },
      { label: 'Website Design', anchor: 'transformations' },
      { label: 'Brand Identity', anchor: 'transformations' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    title: 'Transformations',
    links: [
      { label: 'Crownbisi Tech Ventures', anchor: 'transformations' },
      { label: 'Ejimaco', anchor: 'transformations' },
      { label: 'Everything Suits', anchor: 'transformations' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'AI Business Academy', href: '/academy' },
      { label: 'About', href: '/about' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Transformations', anchor: 'transformations' },
    ],
  },
  {
    title: 'Contact',
    links: [
      { label: 'WhatsApp', href: buildWhatsAppLink(), external: true },
      { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
      { label: 'TikTok', href: TIKTOK_URL, external: true },
    ],
  },
]

export default function Footer({ emailOverride, showGuideLink = true }) {
  const location = useLocation()
  const email = emailOverride || CONTACT_EMAIL
  const columns = COLUMNS.map((col) =>
    col.title === 'Contact'
      ? { ...col, links: col.links.map((l) => (l.label === CONTACT_EMAIL ? { label: email, href: `mailto:${email}` } : l)) }
      : col
  )

  return (
    // pb-28 on mobile, back to pb-10 at md+ — reserves space for the fixed
    // mobile tab bar in Navbar.jsx so it never overlaps the last real
    // content. Scoped here (not a global body rule) because Footer is
    // only used on pages that also render the tab bar — /guide,
    // /getawebsite, /onlinegrowth use MinimalFooter instead and don't
    // need this.
    <footer className="pt-20 pb-28 md:pb-10 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="container-bw">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 pb-14">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <Logo className="h-7 w-7 rounded-md" />
              <span className="font-display text-lg" style={{ color: 'var(--text-primary)' }}>
                Better Website Co.
              </span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Websites That Sell.
            </p>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <p className="eyebrow mb-4">{col.title}</p>
              <ul className="flex flex-col gap-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.anchor ? resolveSectionHref(link.anchor, location.pathname) : link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noreferrer' : undefined}
                      className="text-sm hover:opacity-70 transition-opacity break-words"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="pt-8 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              © {new Date().getFullYear()} Better Website Co. All rights reserved.
            </p>
            {showGuideLink && (
              <Link
                to="/guide"
                className="text-xs hover:opacity-70 transition-opacity"
                style={{ color: 'var(--text-muted)' }}
              >
                Also: our &#8358;2,000 credibility guide &rarr;
              </Link>
            )}
          </div>
          <div className="flex items-center gap-6">
            <p className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
              +{WHATSAPP_NUMBER.replace(/(\d{3})(\d{3})(\d{3})(\d+)/, '$1 $2 $3 $4')}
            </p>
            <Link to="/meet-bee" aria-label="A little secret" className="opacity-60 hover:opacity-100 transition-opacity">
              <Bee size="sm" className="!h-6" animateIn={false} />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
